// Pure STT-utterance → clinical transcript turn mapping. Originally written
// against Deepgram's diarized-utterance shape; the STT provider migrated to
// Sarvam 2026-08-08 (owner order — see server/stt/sarvam.mjs's header). The
// diarization source came back the SAME day (owner corrected the initial
// "Sarvam can't diarize" call): server/stt/sarvam.mjs's
// transcribeBufferDiarized maps Sarvam Batch-API `diarized_transcript`
// entries into exactly this {speaker, transcript, start, end}[] shape, and
// opd-voice.js feeds them through here after each multi-party consult stops.
// The mapping logic is provider-agnostic and unchanged.
// Both server upload/batch flows and native foreground capture use this one
// rule so a session's first distinct voice is Doctor, then Patient, Family and
// Child. A single-speaker recording deliberately returns no diarized turns;
// the caller's known active speaker remains more trustworthy in that case.

const ROLE_ORDER = ['doctor', 'patient', 'family', 'child'];

function cap(value) {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

export function utterancesToClinicalTurns(utterances) {
  if (!Array.isArray(utterances) || !utterances.length) return [];
  const distinct = new Set(
    utterances
      .map((utterance) => (typeof utterance?.speaker === 'number' ? utterance.speaker : null))
      .filter((speaker) => speaker != null),
  );
  if (distinct.size < 2) return [];

  const roleBySpeaker = new Map();
  const roleFor = (speaker) => {
    if (speaker == null) return 'patient';
    if (roleBySpeaker.has(speaker)) return roleBySpeaker.get(speaker);
    const role = ROLE_ORDER[roleBySpeaker.size] || 'family';
    roleBySpeaker.set(speaker, role);
    return role;
  };

  return utterances
    .map((utterance) => {
      const text = String(utterance?.transcript || '').trim();
      if (!text) return null;
      const dgSpeaker = typeof utterance.speaker === 'number' ? utterance.speaker : null;
      const role = roleFor(dgSpeaker);
      const confidence = Number(utterance.confidence);
      return {
        role,
        speaker: cap(role),
        dgSpeaker,
        text,
        startMs: typeof utterance.start === 'number' ? Math.round(utterance.start * 1000) : null,
        endMs: typeof utterance.end === 'number' ? Math.round(utterance.end * 1000) : null,
        confidence: Number.isFinite(confidence) ? confidence : null,
      };
    })
    .filter(Boolean);
}
